import {
  useConnectAccountToOpenSolar,
  useDisconnectAccountFromOpenSolar,
} from '@/api/mutations/integrations/openSolar';
import useGetMyAccountIntegrations from '@/api/queries/me/useGetMyAccountIntegrations';
import { usePermissions } from '@/hooks/usePermissions';
import {
  ConnectionControl,
  ConnectionControlBenefitList,
} from '@/ui/components';
import { registerFieldLegacyBuilder } from '@/utils/zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Modal,
  RawInput,
  useToastContext,
} from '@payaca/ambrosia';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { assertUnreachable } from '@payaca/utilities/guards';
import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import OpenSolarLogo from './openSolar.png';

const formSchema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
});
type FormSchema = z.infer<typeof formSchema>;

export const OpenSolarConnectionControl: FC = () => {
  const [isSettingUpConnection, setIsSettingUpConnection] = useState(false);
  const disconnectAccountFromOpenSolar = useDisconnectAccountFromOpenSolar();
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetMyAccountIntegrations();
  const hasConnection = integrations.some(
    (accountIntegration) => accountIntegration.service === 'OPEN_SOLAR'
  );
  const isLoading = isLoadingIntegrations;
  const { userHasRequiredPermission } = usePermissions();

  const canUpdate = useMemo(() => {
    return userHasRequiredPermission([
      AccountsPermissions.UPDATE_DESIGN_INTEGRATION,
    ]);
  }, [userHasRequiredPermission]);

  return (
    <ConnectionControl
      allowConnectDisconnect={canUpdate && !isLoading}
      connectionName="OpenSolar"
      isConnected={hasConnection}
      onAddConnection={() => setIsSettingUpConnection(true)}
      onRemoveConnection={() => disconnectAccountFromOpenSolar.mutateAsync({})}
      logo={OpenSolarLogo}
    >
      {hasConnection ? (
        <p>Your OpenSolar account has successfully been connected to Payaca.</p>
      ) : (
        <>
          Connect Payaca with OpenSolar for easy syncing of your projects.
          <ConnectionControlBenefitList
            benefits={[
              'Create an OpenSolar project from Payaca.',
              'Sync your files from OpenSolar into Payaca automatically.',
            ]}
          />
        </>
      )}
      <Modal
        isOpen={isSettingUpConnection}
        onClose={() => setIsSettingUpConnection(false)}
        title="Connect your OpenSolar account"
      >
        <OpenSolarConnectionForm
          onSuccess={() => setIsSettingUpConnection(false)}
        />
      </Modal>
    </ConnectionControl>
  );
};

const OpenSolarConnectionForm: FC<{ onSuccess: () => void }> = ({
  onSuccess,
}) => {
  const connectAccountToOpenSolar = useConnectAccountToOpenSolar();
  const { pushSuccess } = useToastContext();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const registerField = registerFieldLegacyBuilder(errors);
  const onSubmit = async (data: FormSchema) => {
    const response = await connectAccountToOpenSolar.mutateAsync(data);

    switch (response.connectAccountToOpenSolar.__typename) {
      case 'ConnectAccountToOpenSolarSuccess': {
        pushSuccess('Your OpenSolar account has been successfully connected.');
        return onSuccess();
      }
      case 'ConnectAccountToOpenSolarInvalidCredentials': {
        return setError('root', {
          type: 'server',
          message:
            'The username or password you entered appear to be invalid. ' +
            'Please check your OpenSolar account and try again.',
        });
      }
      case 'ConnectAccountToOpenSolarTwoFactorCodeRequired': {
        return setError('root', {
          type: 'server',
          message:
            'We were unable to connect to OpenSolar because the user account ' +
            'provided requires two-factor authentication. Please disable ' +
            'two-factor authentication for this account and try again. You ' +
            'can re-enable two-factor authentication after the connection is ' +
            'established.',
        });
      }
      case 'ConnectAccountToOpenSolarError': {
        return setError('root', {
          type: 'server',
          message: 'Sorry, we were unable to add your OpenSolar account.',
        });
      }
      default: {
        assertUnreachable(response?.connectAccountToOpenSolar);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Body>
        <p className="mb-4">
          Please enter your OpenSolar username and password. We will use these
          to connect your Payaca account to OpenSolar, but we will not store
          them.
        </p>
        <p className="mb-4">
          Ensure that the user account has full permissions in OpenSolar,
          otherwise the integration may not work as expected. We recommend
          adding a new team member in OpenSolar specifically for Payaca. This
          will avoid the integration being tied to a particular individual.
        </p>
        <div className="space-y-2.5">
          <Field.Legacy {...registerField('username')}>
            <Field.Label>Username</Field.Label>
            <RawInput
              {...register('username')}
              placeholder="Your OpenSolar username"
              autoComplete="off"
              onChange={(change) => {
                clearErrors('root');
                void register('username').onChange(change);
              }}
            />
          </Field.Legacy>
          <Field.Legacy {...registerField('password')}>
            <Field.Label>Password</Field.Label>
            <RawInput
              {...register('password')}
              type="password"
              placeholder="Your OpenSolar password"
              autoComplete="off"
              onChange={(change) => {
                clearErrors('root');
                void register('password').onChange(change);
              }}
            />
          </Field.Legacy>
          <Field.Legacy {...registerField('root')} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button type="submit" isProcessing={isSubmitting}>
            Connect
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </form>
  );
};
